import { FC } from "react";
import { ToggleGroup, ToggleGroupItem } from "@/lib/shadcn/ui/toggle-group";
import { MeasureValue } from "@/lib/organisms/measure";

// The Measure segmented control (airbnb-review-performance-table AS-12, FR-019).
// A Radix ToggleGroup type="single": one segment is always pressed, never a
// zero-selected state. Selection is controlled — the table (T018) owns it.
// The MeasureValue type + defaultMeasureValue helper live in ./measure.

interface MeasureSegment {
    value: MeasureValue;
    label: string;
}

const ALL_SEGMENTS: MeasureSegment[] = [
    { value: "new-reviews", label: "New Reviews" },
    { value: "cohort", label: "Cohort Score" },
    { value: "lifetime", label: "Lifetime" },
    { value: "drift-prev-period", label: "Drift vs Prev Period" },
    { value: "drift-prev-year", label: "Drift vs Prev Year" },
];

interface MeasureToggleProps {
    value: MeasureValue;
    onValueChange: (value: MeasureValue) => void;
    // FR-019 whole-year collapse: false omits the Drift-vs-Prev-Period segment.
    showPreviousPeriod: boolean;
    // While the query is loading the control is disabled (UI-spec States).
    loading?: boolean;
}

const MeasureToggle: FC<MeasureToggleProps> = ({ value, onValueChange, showPreviousPeriod, loading = false }) => {
    const segments = showPreviousPeriod
        ? ALL_SEGMENTS
        : ALL_SEGMENTS.filter((segment) => segment.value !== "drift-prev-period");

    return (
        <ToggleGroup
            type="single"
            variant="outline"
            data-testid="measure-toggle"
            aria-label="Measure"
            value={value}
            // Radix emits "" when a user tries to deselect the pressed item; ignore
            // it so the control is never in a zero-selected state.
            onValueChange={(next) => {
                if (next) {
                    onValueChange(next as MeasureValue);
                }
            }}
        >
            {segments.map((segment) => (
                <ToggleGroupItem
                    key={segment.value}
                    value={segment.value}
                    data-testid={`measure-toggle-${segment.value}`}
                    aria-label={segment.label}
                    disabled={loading}
                >
                    {segment.label}
                </ToggleGroupItem>
            ))}
        </ToggleGroup>
    );
};

export default MeasureToggle;
